import { Stack, styled } from "@mui/material";
import AcxButton from "components/UI/AcxButton";
import { observer } from "mobx-react";
import React from "react";
import type { KboFlowFinding } from "services/AcxAgentService";
import { AgentState } from "../Models/AgentState";
import { useCurrentChatStore } from "../Stores/useCurrentChat";

const FindingPrompt = styled(AcxButton)(({ theme }) => ({
    // Buttons default to a single truncated line; long prompts wrap instead.
    whiteSpace: "normal",
    textAlign: "start",
    paddingInline: theme.spacing(1.5),
    border: `1px solid ${theme.palette.gray[300]}`,
    borderRadius: theme.spacing(1),
    color: theme.palette.gray[800],
    backgroundColor: theme.palette.white.main,
    fontWeight: 400,
    ":hover": {
        backgroundColor: theme.palette.gray[100],
    },
    ":disabled": {
        color: theme.palette.gray[800],
        backgroundColor: theme.palette.white.main,
        opacity: 0.5,
    },
}));

FindingPrompt.defaultProps = {
    fullWidth: false,
};

/** One prompt per KBo Flow research finding, anchored above a research chat's input. */
export const FindingPrompts = observer(
    ({ fullscreen }: { fullscreen?: boolean }) => {
        const currentChatStore = useCurrentChatStore();
        const findings = currentChatStore.chat.kboFlowFindings;
        if (!findings?.length) return null;

        const ask = (finding: KboFlowFinding) =>
            currentChatStore.streamingSubmitInput({
                // Naming the finding lets KBo show its evidence and conversations.
                manualInput: `${finding.suggestedPrompt}\n\n(About the research finding "${finding.title}", findingId ${finding.findingId}.)`,
                userMessage: finding.suggestedPrompt,
                suggestion: true,
            });

        return (
            <Stack
                direction="row"
                gap={1}
                sx={{
                    flexWrap: "wrap",
                    paddingBottom: 1,
                    paddingInline: fullscreen ? 0 : 2,
                }}
            >
                {findings.map((finding) => (
                    <FindingPrompt
                        key={finding.findingId}
                        id="kbo-finding-prompt"
                        disabled={currentChatStore.state !== AgentState.Idle}
                        onClick={() => ask(finding)}
                    >
                        {finding.suggestedPrompt}
                    </FindingPrompt>
                ))}
            </Stack>
        );
    },
);
